
import { useState, type ChangeEvent, type FormEvent, useEffect } from "react";
import { Box, Button, Card, Checkbox, Divider, FormControlLabel, InputAdornment, TextField, Typography } from "@mui/material";
import { CreditCard as CreditCardIcon, Lock as LockIcon } from "@mui/icons-material";
import { detectCardType, validateCardNumber } from "..//helpers/card-utils";
import type { CardFormData } from "..//types/payment";
import { useFormik } from "formik";
import * as yup from "yup";

interface CardPaymentProps {
	id:string;
	totalValue: number;
	onSubmit: (e: FormEvent, cardData: CardFormData) => void;
	isProcessing: boolean;
	clientName: string;
}

// Interface estendida para incluir o token do cartão
interface CardFormDataWithToken extends CardFormData {
	cardToken: string;
}

// Schema simplificado sem validações complexas
const simpleSchema = (clientName: string) =>
	yup.object({
		cardName: yup.string().required("O nome do titular é obrigatório"),
		cardNumber: yup.string().required("O número do cartão é obrigatório"),
		expiryDate: yup.string().required("A data de validade é obrigatória"),
		cvv: yup.string().required("O código de segurança é obrigatório"),
		saveCard: yup.boolean(),
	});

const CardPayment = ({id, totalValue, onSubmit, isProcessing, clientName }: CardPaymentProps) => {
	const [cardError, setCardError] = useState<string | null>(null);
	const [expiryError, setExpiryError] = useState<string | null>(null);
	const [nameError, setNameError] = useState<string | null>(null);
	const [cardToken, setCardToken] = useState<string>("");
	
	// Adiciona os scripts do PJBank para tokenização
	useEffect(() => {
		// Adiciona o script principal do PJBank
		const script1 = document.createElement('script');
		script1.src = 'https://s3-sa-east-1.amazonaws.com/widgets.superlogica.net/embed.js';
		script1.async = true;
		
		// Adiciona o script de inicialização
		const script2 = document.createElement('script');
		script2.type = 'text/javascript';
		const pjbankCredential = process.env.REACT_APP_PJBANK_CREDENTIAL || "e0727263cc7a983f0aae5411ad86c5a144b8ed28"; // Sandbox por padrão
		const isSandbox = true; // Altere para false no ambiente de produção
	
		script2.innerHTML = `
			superlogica.require("pjbank");
			superlogica.pjbank("checkout_transparente", "${pjbankCredential}", ${isSandbox});
		`;

		// script2.innerHTML = `
		// 	superlogica.require("pjbank");
		// 	superlogica.pjbank("checkout_transparente",
		// 	 "e0727263cc7a983f0aae5411ad86c5a144b8ed28", true);
		// `;
		
		// Adiciona os scripts ao head
		document.head.appendChild(script1);
		
		script1.onload = () => {
			document.head.appendChild(script2);
		};
		
		// Limpa os scripts ao desmontar o componente
		return () => {
			if (document.head.contains(script1)) {
				document.head.removeChild(script1);
			}
			if (document.head.contains(script2)) {
				document.head.removeChild(script2);
			}
		};
	}, []);
	
	// Função para monitorar mudanças no token
	useEffect(() => {
		const tokenInput = document.querySelector('.pjbank-token') as HTMLInputElement;
		
		if (tokenInput) {
			const observer = new MutationObserver((mutations) => {
				mutations.forEach((mutation) => {
					if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
						setCardToken(tokenInput.value);
					}
				});
			});
			
			observer.observe(tokenInput, { attributes: true });
			
			return () => observer.disconnect();
		}
	}, []);

	// Função para verificar se o nome do cartão corresponde ao nome do cliente
	const isNameMatch = (cardName: string, clientName: string): boolean => {
		if (!cardName || !clientName) return false;

		// Normaliza os nomes (remove espaços extras, converte para minúsculas)
		const normalizedCardName = cardName.trim().toLowerCase();
		const normalizedClientName = clientName.trim().toLowerCase();

		// Se os nomes são idênticos, retorna true
		if (normalizedCardName === normalizedClientName) return true;

		// Divide os nomes em partes
		const cardNameParts = normalizedCardName.split(" ").filter((part) => part.length > 0);
		const clientNameParts = normalizedClientName.split(" ").filter((part) => part.length > 0);

		// Verifica se pelo menos o primeiro e último nome correspondem
		if (cardNameParts.length >= 2 && clientNameParts.length >= 2) {
			const cardFirstName = cardNameParts[0];
			const cardLastName = cardNameParts[cardNameParts.length - 1];
			const clientFirstName = clientNameParts[0];
			const clientLastName = clientNameParts[clientNameParts.length - 1];

			// Se o primeiro e último nome correspondem, é uma correspondência válida
			if (cardFirstName === clientFirstName && cardLastName === clientLastName) {
				return true;
			}
		}

		// Verifica se o nome do cartão pode ser uma versão abreviada do nome do cliente
		if (cardNameParts.length < clientNameParts.length) {
			// Primeiro nome deve corresponder
			if (cardNameParts[0] !== clientNameParts[0]) return false;

			// Último nome deve corresponder
			if (cardNameParts[cardNameParts.length - 1] !== clientNameParts[clientNameParts.length - 1]) return false;

			// Verifica se os nomes do meio são iniciais ou abreviações
			for (let i = 1; i < cardNameParts.length - 1; i++) {
				const cardMiddleName = cardNameParts[i];

				// Se for uma inicial (uma letra seguida ou não por ponto)
				if (cardMiddleName.length <= 2 && (cardMiddleName.length === 1 || cardMiddleName.endsWith("."))) {
					// Verifica se corresponde à inicial de algum nome do meio do cliente
					let foundMatch = false;
					for (let j = 1; j < clientNameParts.length - 1; j++) {
						if (clientNameParts[j].startsWith(cardMiddleName.charAt(0))) {
							foundMatch = true;
							break;
						}
					}
					if (!foundMatch) return false;
				} else {
					// Se não for uma inicial, deve corresponder exatamente a um dos nomes do meio
					let foundMatch = false;
					for (let j = 1; j < clientNameParts.length - 1; j++) {
						if (clientNameParts[j] === cardMiddleName) {
							foundMatch = true;
							break;
						}
					}
					if (!foundMatch) return false;
				}
			}

			return true;
		}

		return false;
	};

	const formik = useFormik({
		initialValues: {
			cardNumber: "",
			cardName: "",
			expiryDate: "",
			cvv: "",
			saveCard: false,
		},
		validationSchema: simpleSchema(clientName),
		onSubmit: (values, { setSubmitting }) => {
			// Validação manual do nome
			if (!isNameMatch(values.cardName, clientName)) {
				setNameError("O nome do titular deve corresponder ao nome do cliente (pelo menos primeiro e último nome)");
				return;
			}

			// Validação manual do número do cartão
			if (!/^[0-9\s]+$/.test(values.cardNumber)) {
				setCardError("O número do cartão deve conter apenas números");
				return;
			}

			const cardNumberWithoutSpaces = values.cardNumber.replace(/\s/g, "");
			if (cardNumberWithoutSpaces.length < 13 || cardNumberWithoutSpaces.length > 19) {
				setCardError("O número do cartão deve ter entre 13 e 19 dígitos");
				return;
			}

			if (!validateCardNumber(values.cardNumber)) {
				setCardError("Número de cartão inválido");
				return;
			}

			// Validação manual da data de validade
			if (!/^(0[1-9]|1[0-2])\/([0-9]{4})$/.test(values.expiryDate)) {
				setExpiryError("Formato inválido. Use MM/AAAA");
				return;
			}

			// Limpa os erros
			setCardError(null);
			setExpiryError(null);
			setNameError(null);

			// Verifica se o token foi gerado
			if (!cardToken) {
				setCardError("Não foi possível gerar o token do cartão. Verifique os dados e tente novamente.");
				return;
			}
			
			// Envia os dados com o token para o componente pai
			onSubmit(new Event("submit") as unknown as FormEvent, {
				cardNumber: values.cardNumber,
				cardName: values.cardName,
				expiryDate: values.expiryDate,
				cvv: values.cvv,
				saveCard: values.saveCard,
				cardToken: cardToken
			} as CardFormDataWithToken);
		},
	});

	const handleCardNumberChange = (e: ChangeEvent<HTMLInputElement>) => {
		// Limpa o erro do cartão quando o usuário começa a digitar novamente
		setCardError(null);

		// Garantir que apenas números sejam aceitos
		const inputValue = e.target.value.replace(/[^0-9]/g, "");

		// Limita a 19 dígitos (padrão máximo para cartões)
		const limitedValue = inputValue.slice(0, 19);

		// Formata o número do cartão com espaços a cada 4 dígitos
		let formattedValue = "";
		for (let i = 0; i < limitedValue.length; i += 4) {
			formattedValue += limitedValue.substring(i, i + 4) + " ";
		}

		// Remove o espaço extra no final
		formattedValue = formattedValue.trim();

		formik.setFieldValue("cardNumber", formattedValue, false);
	};

	const handleExpiryDateChange = (e: ChangeEvent<HTMLInputElement>) => {
		// Limpa o erro da data quando o usuário começa a digitar novamente
		setExpiryError(null);

		// Garantir que apenas números sejam aceitos
		const inputValue = e.target.value.replace(/[^0-9/]/g, "");

		// Formata a data de validade
		let formattedValue = inputValue;

		// Se o usuário digitou 2 dígitos sem barra, adiciona a barra automaticamente
		if (inputValue.length === 2 && !inputValue.includes("/")) {
			formattedValue = `${inputValue}/`;
		}
		// Se o usuário digitou mais de 2 dígitos sem barra, formata como MM/AA
		else if (inputValue.length > 2 && !inputValue.includes("/")) {
			formattedValue = `${inputValue.substring(0, 4)}/${inputValue.substring(4)}`;
		}

		// Limita a 5 caracteres (MM/AA)
		if (formattedValue.length > 5) {
			formattedValue = formattedValue.substring(0, 7);
		}

		formik.setFieldValue("expiryDate", formattedValue, false);
	};

	const handleCvvChange = (e: ChangeEvent<HTMLInputElement>) => {
		// Garantir que apenas números sejam aceitos
		const inputValue = e.target.value.replace(/[^0-9]/g, "");
		// Limitar a 4 dígitos
		const limitedValue = inputValue.slice(0, 4);
		formik.setFieldValue("cvv", limitedValue, false);
	};

	const handleCardNameChange = (e: ChangeEvent<HTMLInputElement>) => {
		// Limpa o erro do nome quando o usuário começa a digitar novamente
		setNameError(null);
		formik.handleChange(e);
	};

	const cardType = detectCardType(formik.values.cardNumber.replace(/\s+/g, ""));

	return (
		<Card sx={{ p: 3, flex: 1, width: { xs: "100%", md: "50%" } }}>
			<Typography variant="h6" gutterBottom>
				Dados de Pagamento
			</Typography>

			<form onSubmit={formik.handleSubmit}>
				{/* Bandeiras de cartão */}
				<Box sx={{ mb: 3, display: "flex", justifyContent: "center", gap: 2 }}>
					<Box
						component="img"
						src="https://via.placeholder.com/45x30"
						alt="Visa"
						sx={{
							filter: "grayscale(0.2)",
							opacity: 0.9,
							transition: "all 0.2s",
							"&:hover": {
								filter: "grayscale(0)",
								opacity: 1,
								transform: "scale(1.05)",
							},
						}}
					/>
					<Box
						component="img"
						src="https://via.placeholder.com/45x30"
						alt="Mastercard"
						sx={{
							filter: "grayscale(0.2)",
							opacity: 0.9,
							transition: "all 0.2s",
							"&:hover": {
								filter: "grayscale(0)",
								opacity: 1,
								transform: "scale(1.05)",
							},
						}}
					/>
					<Box
						component="img"
						src="https://via.placeholder.com/45x30"
						alt="American Express"
						sx={{
							filter: "grayscale(0.2)",
							opacity: 0.9,
							transition: "all 0.2s",
							"&:hover": {
								filter: "grayscale(0)",
								opacity: 1,
								transform: "scale(1.05)",
							},
						}}
					/>
					<Box
						component="img"
						src="https://via.placeholder.com/45x30"
						alt="Elo"
						sx={{
							filter: "grayscale(0.2)",
							opacity: 0.9,
							transition: "all 0.2s",
							"&:hover": {
								filter: "grayscale(0)",
								opacity: 1,
								transform: "scale(1.05)",
							},
						}}
					/>
					<Box
						component="img"
						src="https://via.placeholder.com/45x30"
						alt="Hipercard"
						sx={{
							filter: "grayscale(0.2)",
							opacity: 0.9,
							transition: "all 0.2s",
							"&:hover": {
								filter: "grayscale(0)",
								opacity: 1,
								transform: "scale(1.05)",
							},
						}}
					/>
				</Box>

				<Divider sx={{ mb: 3 }} />

				{/* Nome do Titular - Primeira linha */}
				<Box sx={{ mb: 3 }}>
					<Typography variant="subtitle2" gutterBottom sx={{ mb: 1 }}>
						Nome do Titular
					</Typography>
					<TextField
						fullWidth
						variant="outlined"
						id="cardName"
						name="cardName"
						value={formik.values.cardName}
						onChange={handleCardNameChange}
						onBlur={formik.handleBlur}
						placeholder="Como aparece no cartão"
						required
						size="small"
						error={(formik.touched.cardName && Boolean(formik.errors.cardName)) || Boolean(nameError)}
						helperText={(formik.touched.cardName && formik.errors.cardName) || nameError}
					/>
				</Box>

				{/* Número do Cartão - Segunda linha */}
				<Box sx={{ mb: 3 }}>
					<Typography variant="subtitle2" gutterBottom sx={{ mb: 1 }}>
						Número do Cartão
					</Typography>
					<TextField
						fullWidth
						variant="outlined"
						id="cardNumber"
						name="cardNumber"
						value={formik.values.cardNumber}
						onChange={formik.handleChange}	// {handleCardNumberChange}
						onBlur={formik.handleBlur}
						placeholder="1234 5678 9012 3456"
						required
						size="small"
						error={(formik.touched.cardNumber && Boolean(formik.errors.cardNumber)) || Boolean(cardError)}
						helperText={(formik.touched.cardNumber && formik.errors.cardNumber) || cardError}
						InputProps={{
							endAdornment: cardType ? (
								<InputAdornment position="end">
									<Typography variant="caption" color="primary" fontWeight="medium">
										{cardType}
									</Typography>
								</InputAdornment>
							) : null,
							startAdornment: (
								<InputAdornment position="start">
									<CreditCardIcon sx={{ color: "text.secondary" }} />
								</InputAdornment>
							),
							inputProps: {
								inputMode: "numeric",
								maxLength: 23, // 19 dígitos + 4 espaços
								className: "pjbank-cartao" // Adiciona a classe para tokenização
							},
						}}
					/>
					{/* Campo oculto para armazenar o token do cartão */}
					<input type="hidden" className="pjbank-token" />
				</Box>

				{/* Validade e CVV - Terceira linha */}
				<Box sx={{ mb: 3, display: "flex", gap: 2 }}>
					<Box sx={{ flex: 1 }}>
						<Typography variant="subtitle2" gutterBottom sx={{ mb: 1 }}>
							Validade
						</Typography>
						<TextField
							fullWidth
							variant="outlined"
							id="expiryDate"
							name="expiryDate"
							value={formik.values.expiryDate}
							onChange={handleExpiryDateChange}
							onBlur={formik.handleBlur}
							placeholder="MM/AAAA"
							required
							size="small"
							error={(formik.touched.expiryDate && Boolean(formik.errors.expiryDate)) || Boolean(expiryError)}
							helperText={(formik.touched.expiryDate && formik.errors.expiryDate) || expiryError}
							InputProps={{
								inputProps: {
									inputMode: "numeric",
									maxLength:7,
								},
							}}
						/>
					</Box>
					<Box sx={{ flex: 1 }}>
						<Typography variant="subtitle2" gutterBottom sx={{ mb: 1 }}>
							CVV
						</Typography>
						<TextField
							fullWidth
							variant="outlined"
							id="cvv"
							name="cvv"
							value={formik.values.cvv}
							onChange={handleCvvChange}
							onBlur={formik.handleBlur}
							placeholder="123"
							type="password"
							required
							size="small"
							error={formik.touched.cvv && Boolean(formik.errors.cvv)}
							helperText={formik.touched.cvv && formik.errors.cvv}
							InputProps={{
								inputProps: {
									inputMode: "numeric",
									maxLength: 4,
								},
							}}
						/>
					</Box>
				</Box>

				<FormControlLabel
					control={
						<Checkbox id="saveCard" name="saveCard" checked={formik.values.saveCard} onChange={formik.handleChange} color="primary" size="small" />
					}
					label="Salvar cartão para pagamentos futuros"
					sx={{ mb: 2 }}
				/>

				<Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
					<LockIcon sx={{ color: "text.secondary", mr: 1, fontSize: 16 }} />
					<Typography variant="caption" color="text.secondary">
						Seus dados de pagamento estão seguros e criptografados
					</Typography>
				</Box>

				<Box
					sx={{
						p: 2,
						mb: 3,
						bgcolor: "rgba(138, 43, 226, 0.03)",
						borderRadius: 1,
						border: "1px solid rgba(138, 43, 226, 0.1)",
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
					}}
				>
					<Typography variant="subtitle2">Valor Total:</Typography>
					<Typography variant="h6" fontWeight="bold" color="primary.main">
						R$ {totalValue?.toFixed(2).replace(".", ",") || "0,00"}
					</Typography>
				</Box>

				<Button
					type="submit"
					variant="contained"
					color="primary"
					fullWidth
					size="large"
					disabled={isProcessing}
					sx={{
						py: 1.5,
						position: "relative",
						overflow: "hidden",
					}}
				>
					{isProcessing ? (
						<>
							<Box
								sx={{
									position: "absolute",
									left: 0,
									top: 0,
									width: "100%",
									height: "100%",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									backgroundColor: "primary.main",
								}}
							>
								<Box
									sx={{
										width: 20,
										height: 20,
										borderRadius: "50%",
										border: "2px solid rgba(255,255,255,0.3)",
										borderTopColor: "white",
										animation: "spin 1s linear infinite",
										"@keyframes spin": {
											"0%": { transform: "rotate(0deg)" },
											"100%": { transform: "rotate(360deg)" },
										},
									}}
								/>
								<Typography variant="body2" sx={{ ml: 1, color: "white" }}>
									Processando...
								</Typography>
							</Box>
						</>
					) : (
						"Finalizar Pagamento"
					)}
				</Button>
			</form>
		</Card>
	);
};

export default CardPayment;
